import { enumSchemaFromDynamicArray } from '@common/neo.tax';
import { z } from 'zod';

import { SortDirection } from '@neo.tax/shared/graphql';
import { FilterSortDataType } from '@neo.tax/shared/utils';

export const makeSortingSchema = <ColumnNames extends string[]>(
  columnNames: ColumnNames,
): z.ZodType<Sorting<ColumnNames[number]>[]> =>
  z.array(
    z.object({
      id: z.string().describe('For draggable and making direction nullable.'),
      column: enumSchemaFromDynamicArray(columnNames),
      direction: z.enum(SortDirection),
    }),
  );

// id is for draggable, and making direction nullable.
export type Sorting<ColumnName extends string> = {
  id: string;
  column: ColumnName;
  direction: SortDirection;
};

export type ColumnDefinition<ColumnName extends string> = {
  name: ColumnName;
  label: string;
} & (
  | {
      dataType: Exclude<FilterSortDataType, FilterSortDataType.ENUM>;
    }
  | {
      dataType: FilterSortDataType.ENUM;
      enum: { value: string; label: string }[];
    }
) &
  (
    | {
        /**
         * Converts the filter API value to a human-readable value. Should be
         * the inverse of {@link ColumnDefinition.fromHuman|fromHuman}.
         */
        toHuman: (filterValue: string | number | null) => string | number | null;
        /**
         * Converts the human-readable (and human-written) value to a filter API
         * value. Should be the inverse of
         * {@link ColumnDefinition.toHuman|toHuman}.
         */
        fromHuman: (humanValue: string | number | null) => string | number | null;
      }
    | {
        toHuman?: never;
        fromHuman?: never;
      }
  );

export interface SortingState<ColumnName extends string> {
  /** The currently selected sorts */
  sorts: Sorting<ColumnName>[];

  /** The available columns, in display order */
  columns: readonly ColumnDefinition<ColumnName>[];

  /** Resets the sorts */
  resetSorts: () => void;

  /** Sets the selected sorts */
  setSorts: (newSorting: Sorting<ColumnName>[]) => void;
}
